import * as React from 'react';
import { View } from 'react-native';

import { Text } from '@/components/ui/text';
import { SetupLayout } from '@/features/setup/components/setup-layout';
import {
  FACET_LOAD_ERROR,
  useSetupFacetOptions,
} from '@/features/setup/hooks/use-setup-facet-options';
import { useSetupContext } from '@/features/setup/setup-provider';
import { FacetChipGrid, type FacetOption } from '@/features/taste-profile/components/facet-chip-grid';
import { useTasteProfile } from '@/features/taste-profile/hooks/use-taste-profile';

const STEP = 4;

interface FacetSectionProps {
  title: string;
  options: FacetOption[];
  hasError: boolean;
  isSelected: (option: FacetOption) => boolean;
  onToggle: (option: FacetOption) => void;
  testIdPrefix: string;
}

/**
 * One labelled facet. A facet that failed to load says so quietly instead of
 * showing an empty grid; the step stays skippable either way.
 */
function FacetSection({
  title,
  options,
  hasError,
  isSelected,
  onToggle,
  testIdPrefix,
}: FacetSectionProps) {
  return (
    <>
      <Text className="mb-2 text-base font-semibold text-foreground">{title}</Text>
      <View className="mb-6">
        {hasError ? (
          <Text testID={`${testIdPrefix}-error`} className="text-sm text-muted-foreground">
            {FACET_LOAD_ERROR}
          </Text>
        ) : (
          <FacetChipGrid
            options={options}
            isSelected={isSelected}
            onToggle={onToggle}
            testIdPrefix={testIdPrefix}
          />
        )}
      </View>
    </>
  );
}

export function TastePreferencesScreen() {
  const { advance, goBack, isAdvancing } = useSetupContext();
  const { data, toggleCuisine, toggleAllergen, toggleDietary } = useTasteProfile();
  const { cuisines, allergens, dietaryProfiles, errors } = useSetupFacetOptions();

  const cuisineSelected = (option: FacetOption) =>
    data.cuisines.some((c) => c.id === option.id && c.valence === 'favorite');
  const allergenSelected = (option: FacetOption) => data.allergens.some((a) => a.id === option.id);
  const dietarySelected = (option: FacetOption) =>
    data.dietaryProfiles.some((d) => d.id === option.id);

  return (
    <SetupLayout
      step={STEP}
      onBack={goBack}
      primaryLabel="Continue"
      onPrimary={advance}
      primaryLoading={isAdvancing}
    >
      <Text className="mb-2 text-xl font-bold text-foreground">Taste Preferences</Text>
      <Text className="mb-4 text-sm text-muted-foreground">
        Pick a few favorites for better recommendations. You can skip anything and refine it later
        in Settings.
      </Text>

      <FacetSection
        title="Favorite cuisines"
        options={cuisines}
        hasError={errors.cuisines}
        isSelected={cuisineSelected}
        onToggle={(option) => toggleCuisine(option, 'favorite')}
        testIdPrefix="onboarding-cuisine"
      />

      <FacetSection
        title="Any allergies?"
        options={allergens}
        hasError={errors.allergens}
        isSelected={allergenSelected}
        onToggle={toggleAllergen}
        testIdPrefix="onboarding-allergen"
      />

      <FacetSection
        title="How you eat"
        options={dietaryProfiles}
        hasError={errors.dietaryProfiles}
        isSelected={dietarySelected}
        onToggle={toggleDietary}
        testIdPrefix="onboarding-dietary"
      />
    </SetupLayout>
  );
}
